import React, { useState } from 'react';
import { yupResolver } from '@hookform/resolvers';
import { useForm } from 'react-hook-form';
import { useHistory, useParams } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import ChangePasswordForm, { userSchema } from './ChangePasswordForm';
import { ErrorAlert } from 'components/Alert';
import {
  ChangePasswordData,
  ChangePasswordRequest,
  ChangePasswordResult,
  Login
} from 'services/rest/authentication';
import { login } from 'redux/slices/userSlice';
import { getCurrentCompany } from 'helpers/companyHelper';

interface Token {
  token: string;
}

// El payload del token de reset ya indica si la cuenta es de administrador
// (`reset-password-administrator`). No se valida la firma a proposito: esto
// solo decide con que identificador se intenta el auto-login, la
// autenticacion real la resuelve el backend.
const isAdministratorToken = (resetToken: string): boolean => {
  try {
    const payload = resetToken.split('.')[1];
    if (!payload) {
      return false;
    }
    const normalized = payload.replace(/-/g, '+').replace(/_/g, '/');
    return (
      JSON.parse(atob(normalized))?.role === 'reset-password-administrator'
    );
  } catch {
    return false;
  }
};

const ChangePasswordPage: React.FC = () => {
  const { register, handleSubmit, errors } = useForm<ChangePasswordData>({
    resolver: yupResolver(userSchema)
  });
  const history = useHistory();
  const dispatch = useDispatch();
  const subdomain = getCurrentCompany();
  const [backendError, setBackendError] = useState<string | null>(null);
  const [loader, setLoader] = useState(false);
  const { token } = useParams<Token>();
  const [showPassword, setShowPassword] = useState(false);

  const onSubmit = async (changePasswordData: ChangePasswordData) => {
    changePasswordData.token = token;
    if (changePasswordData.password !== changePasswordData.confirmPassword) {
      return;
    }

    setLoader(true);
    const { data, errors } = await ChangePasswordRequest(changePasswordData);

    if (errors || !data) {
      setLoader(false);
      setBackendError(errors?.data);
      return;
    }

    const result: ChangePasswordResult = data.data;
    const { password } = changePasswordData;

    // Los administradores NO pueden autenticarse con su email: el backend
    // ramifica por formato del input y la rama email consulta unicamente
    // `ambassadors`. Por eso el auto-login usa el username cuando el token
    // de reset corresponde a un administrador.
    const isAdministrator =
      result.role === 'administrator' || isAdministratorToken(token);
    const identifier = isAdministrator ? result.username : result.email;

    // Backend sin el campo `username` (deploy desfasado): evitamos el
    // auto-login que sabemos que va a fallar y lo mandamos al login, que es
    // donde el administrador puede entrar con su usuario.
    if (!identifier) {
      setLoader(false);
      history.replace('/sign_in', { from: 'password_updated' });
      return;
    }

    const { user, errors: loginErrors } = await Login({
      email: identifier,
      password,
      subdomain
    });

    if (user) {
      dispatch(login(user));
      history.replace('/dashboard');
      return;
    }

    // El loader se mantiene hasta aca a proposito: apagarlo antes del login
    // dejaba la pantalla quieta varios segundos y parecia colgada.
    setLoader(false);
    if (loginErrors) {
      setBackendError(loginErrors);
    }
  };

  return (
    <>
      {backendError && (
        <ErrorAlert
          text={backendError}
          allowClose={true}
          onClose={() => setBackendError(null)}
        />
      )}

      <ChangePasswordForm
        onSubmit={handleSubmit(onSubmit)}
        register={register}
        errors={errors}
        loader={loader}
        showPassword={showPassword}
        onClickEye={() => {
          setShowPassword(!showPassword);
        }}
      />
    </>
  );
};

export default ChangePasswordPage;
